// Helpers for bulk compose. Kept out of the component so they can be exercised
// directly — the parsing is the part most likely to surprise, since it runs on
// whatever a volunteer pasted out of a bulletin or an email.

/**
 * Split a paste into individual announcements.
 *
 * Blank lines are the strongest signal and win when present, because a real
 * announcement often wraps over two or three lines and splitting those apart
 * would produce nonsense slides. With no blank lines, one per line is the only
 * sensible reading.
 */
export function splitAnnouncements(raw: string): string[] {
  const stripBullet = (s: string) =>
    // Bulleted and numbered lists both survive a copy-paste out of Word.
    s.replace(/^\s*(?:[-*+•·▪]|\d{1,2}[.)])\s+/, "").trim();

  const byBlank = raw
    .split(/\n\s*\n/)
    .map((block) =>
      block
        .split("\n")
        .map(stripBullet)
        .filter(Boolean)
        .join(" ")
        .trim(),
    )
    .filter(Boolean);
  if (byBlank.length > 1) return byBlank;

  return raw.split("\n").map(stripBullet).filter(Boolean);
}

/** Words that make a truncated name read as broken if it ends on one —
 *  "Youth night, Wednesday Jan 14 at" wants to be "…Jan 14". */
const TRAILING_FILLER = new Set([
  "at", "in", "on", "the", "a", "an", "for", "to", "and", "with",
  "from", "by", "of", "our", "this", "is", "will", "be",
]);

/** A short, recognisable name for the Content list — the first few words. */
export function nameFor(text: string, index: number): string {
  const words = text.replace(/\s+/g, " ").trim().split(" ").slice(0, 6);
  // Drop filler off the end, but never everything: a name of "" is worse than
  // an awkward one.
  while (
    words.length > 1 &&
    TRAILING_FILLER.has(words[words.length - 1].toLowerCase().replace(/[^a-z]/g, ""))
  ) {
    words.pop();
  }
  const name = words
    .join(" ")
    // A trailing comma or dash reads as a truncation bug in a list of names.
    .replace(/[,.;:—–-]+$/, "")
    .trim();
  return name || `Slide ${index + 1}`;
}

/**
 * Run `work` over `items` with a fixed number in flight.
 *
 * Rejections are the caller's to handle inside `work`; a throw here would
 * abandon the remaining items, and in a batch of slides the ones that succeeded
 * are still worth having.
 */
export async function pool<T>(
  items: T[],
  limit: number,
  work: (item: T, index: number) => Promise<void>,
): Promise<void> {
  let next = 0;
  const runners = Array.from(
    { length: Math.max(1, Math.min(limit, items.length)) },
    async () => {
      for (;;) {
        const i = next++;
        if (i >= items.length) return;
        await work(items[i], i);
      }
    },
  );
  await Promise.all(runners);
}
